import { useRef } from 'react';
import { Alert, Box, Button, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { formatFileSize, MAX_RECEIPT_MB, RECEIPT_ACCEPT, receiptFileProblem } from '../../utils/paymentReceipts';

/** "Upload Payment Receipt / Bank Slip" (Phase 107) — pick one PDF, JPG or
 * PNG; shows the file's name and size, a validation error, and upload
 * progress while saving. The parent owns the file and the progress. */
export const ReceiptFilePicker = ({
  file,
  onChange,
  error,
  onError,
  progress,
  disabled,
  label = 'Upload Payment Receipt / Bank Slip',
  optional = true,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  error: string | null;
  onError: (error: string | null) => void;
  /** 0–100 while uploading, else null. */
  progress: number | null;
  disabled?: boolean;
  label?: string;
  optional?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {label}
        {optional ? ' (optional)' : ''}
      </Typography>
      <input
        ref={inputRef}
        type="file"
        accept={RECEIPT_ACCEPT}
        hidden
        data-testid="receipt-file-input"
        onChange={(e) => {
          const picked = e.target.files?.[0] ?? null;
          e.target.value = '';
          if (!picked) return;
          const problem = receiptFileProblem(picked);
          onError(problem);
          onChange(problem ? null : picked);
        }}
      />
      {file ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75 }}>
          <AttachFileIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, wordBreak: 'break-all' }} data-testid="receipt-file-name">
            {file.name} · {formatFileSize(file.size)}
          </Typography>
          <IconButton size="small" aria-label="Remove receipt" disabled={disabled} onClick={() => onChange(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <Button variant="outlined" size="small" startIcon={<AttachFileIcon />} disabled={disabled} onClick={() => inputRef.current?.click()}>
          Choose file
        </Button>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        PDF, JPG or PNG, up to {MAX_RECEIPT_MB} MB.
      </Typography>
      {progress !== null && file && (
        <LinearProgress variant="determinate" value={progress} sx={{ mt: 1, borderRadius: 1 }} aria-label="Upload progress" />
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => onError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
